import * as React from 'react';
import * as cx from 'classnames';
import SuperCritical, { SuperCriticalComponent } from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';
import { tzmoment } from 'lib/tzmoment';

import { ReservationsListBase, RowModel, Columns, PageAction, CancellationRemarksPrompt } from './model'

import { ActionsMenuButton } from 'portcall/actionsmenubutton/view';
import { DateRangeSelectorView } from 'portcall/daterangeselector/view';
import { DownloadLink } from 'portcall/downloadlink/view';
import { GridTableHeaderCells, GridTableRowCells, RowSelectorView } from 'portcall/gridcomponents/view';
import { OrgHeaderView } from 'portcall/orgprofile/orgheaderview';
import { PageLoadingIndicator, ActionLoadingIndicator } from 'core/actionloadingindicator/view';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { PCModal } from 'portcall/pcmodal/view';

type actionsMenuFactory = (params : ActionsMenuFactoryParams) => React.ReactElement<any> | null;
export interface ActionsMenuFactoryParams {
    printMenuItem : React.ReactElement<any> | null;
    csvExportMenuItem: React.ReactElement<any> | null;
    cancelReservationsMenuItem : React.ReactElement<any> | null;
    bookReservationsMenuItem : React.ReactElement<any> | null;
    adminSeparator : React.ReactElement<any> | null;
}

export interface ReservationsListBaseProps<TModel> {
    model : ReservationsListBase<TModel>;
    className? : string;
    title? : () => string;
    actionsMenuFactory? : actionsMenuFactory;
    csvExportFileName? : () => string;
}

export class ReservationsListBaseView<TModel> extends SuperCriticalComponent<ReservationsListBaseProps<TModel>> {
    srender(props : ReservationsListBaseProps<TModel>) {
        // This is one way to reference generic React components. An alternative is to nongeneric subclasses. See https://github.com/Microsoft/TypeScript/issues/3960.
        const { model: m, className, title } = props;
        return (
            <div className={cx('reservations-list-base', className)}>
                <div className="pc-actions-links-header">
                    <a className="pc-back-link" onClick={noDefault(m.GoBack)}>&lt;&lt; Back</a>
                    <ActionsMenuButton onClick={() => m.ShowActionsMenu(!m.ShowActionsMenu())} />
                    <ActionsContainer<TModel> {...props} />
                </div>
                {m.Organization() && <OrgHeaderView organization={m.Organization()!} />}
                <PageActionConfirmSelect<TModel> model={m} />
                <ControlsView<TModel> model={m} title={title} />
                <PageLoadingIndicator loading={() => m.Loading() || m.BookingOrCancelling()} />
                <ReservationsTable<TModel> model={m} />
                <CancellationRemarksModal<TModel> model={m} />
            </div>
        );
    }
}

class ActionsContainer<TModel> extends SuperCriticalComponent<ReservationsListBaseProps<TModel>> {
    srender({ model: m, actionsMenuFactory, csvExportFileName } : ReservationsListBaseProps<TModel>) {
        const factory = actionsMenuFactory || defaultActionsMenuFactory;
        return (
            <div className="pc-actions">
                {m.ShowActionsMenu() &&
                    <PCDismissible className="pc-card pc-actions-menu" onDismiss={() => m.ShowActionsMenu(false)}>
                        {factory({
                            printMenuItem: <PrintMenuItem<TModel> model={m} />,
                            csvExportMenuItem: <CSVExportMenuItem<TModel> model={m} csvExportFileName={csvExportFileName} />,
                            cancelReservationsMenuItem: <CancelReservationsMenuItem<TModel> model={m} />,
                            bookReservationsMenuItem: <BookReservationsMenuItem<TModel> model={m} />,
                            adminSeparator: <AdminSeparator />
                        })}
                    </PCDismissible>
                }
            </div>
        );

        function defaultActionsMenuFactory({
            printMenuItem,
            csvExportMenuItem,
            cancelReservationsMenuItem,
            bookReservationsMenuItem,
            adminSeparator
        } : ActionsMenuFactoryParams) {
            return (
                <div>
                    {printMenuItem}
                    {csvExportMenuItem}
                    {cancelReservationsMenuItem}
                    {m.AnyBookableRows() && adminSeparator}
                    {bookReservationsMenuItem}
                </div>
            );
        }
    }
}

class PrintMenuItem<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel> }> {
    srender({ model: m } : { model : ReservationsListBase<TModel> }) {
        return (
            <div className="action-item">
                <div>
                    <a href="" onClick={noDefault(() => {
                            m.ShowActionsMenu(false);
                            window.print();
                        })}>Print Report</a>
                </div>
                <div className="pc-text-smalllabel pc-action-item-descr">
                    Print the current view's results.
                </div>
            </div>
        );
    }
}

class CSVExportMenuItem<TModel> extends SuperCriticalComponent<ReservationsListBaseProps<TModel>> {
    srender({ model: m, csvExportFileName } : ReservationsListBaseProps<TModel>) {
        return (
            <div className="action-item">
                <div>
                    <DownloadLink href={m.CSVExportLink()}
                        download={csvExportFileName ? csvExportFileName() : 'reservations.csv'}
                        onClick={() => m.ShowActionsMenu(false)}>Export to CSV</DownloadLink>
                </div>
                <div className="pc-text-smalllabel pc-action-item-descr">
                    Save the current view's results as a CSV, which can be used in Excel.
                </div>
            </div>
        );
    }
}

class CancelReservationsMenuItem<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel> }> {
    srender({ model: m } : { model : ReservationsListBase<TModel> }) {
        return m.AnyCancellableRows() ?
            <div className="action-item">
                <div>
                    <a href="" onClick={noDefault(m.SelectRowsToCancel)}>Cancel Reservations</a>
                </div>
                <div className="pc-text-smalllabel pc-action-item-descr">Select the reservations to be cancelled.</div>
            </div>
        : null;
    }
}

const AdminSeparator = SuperCritical(() =>
    <div className="pc-admin-separator pc-card-admin-title">
        Admin <div className="pc-admin-line"></div>
    </div>
);

class BookReservationsMenuItem<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel> }> {
    srender({ model: m } : { model : ReservationsListBase<TModel> }) {
        return m.AnyBookableRows() ?
            <div className="action-item">
                <div>
                    <a href="" onClick={noDefault(m.SelectRowsToBook)}>Book Reservations</a>
                </div>
                <div className="pc-text-smalllabel pc-action-item-descr">
                    Select the reservations to be booked. Overlapping reservations in conflict will be changed to Booked.
                </div>
            </div>
            : null;
    }
}

class PageActionConfirmSelect<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel> }> {
    srender({ model: m } : { model : ReservationsListBase<TModel> }) {
        return (m.PageAction() === PageAction.Book || m.PageAction() === PageAction.Cancel) ?
            <div className="page-action-confirm-select">
                <PageActionConfirm<TModel> model={m} />
                <RowSelectorView model={m.RowSelector} />
            </div>
            : null;
    }
}

class PageActionConfirm<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel> }> {
    srender({ model: m } : { model : ReservationsListBase<TModel> }) {
        return (
            <span className="page-action-confirm">
                <span className="are-you-sure">Are you sure?</span>
                {/* Don't allow No click while book or cancel operation in progress */}
                <a href="" className="no" onClick={noDefault(() => !m.BookingOrCancelling() && m.PageAction(PageAction.View))}>No</a>
                <span className="pc-icon-bullet"></span>
                <a href="" className="yes" onClick={noDefault(m.PageAction() === PageAction.Book ? m.BookSelectedReservations : m.PromptForCancellationRemarks)}>Yes</a>
            </span>
        );
    }
}

class ControlsView<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel>, title? : () => string }> {
    srender({ model: m, title } : { model : ReservationsListBase<TModel>, title? : () => string }) {
        return (
            <div className="controls pc-search-mobile">
                <div className="title">{title ? title() : 'Reservations'}</div>
                <input className="pc-reports-filter" placeholder="Filter By &mdash;"
                    value={m.Filter()}
                    onChange={e => m.Filter(e.currentTarget.value)} />
                <DateRangeSelectorView model={m.DateRangeSelector} />
            </div>
        );
    }
}

class ReservationsTable<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel> }> {
    srender({ model: m } : { model : ReservationsListBase<TModel> }) {
        return (
            <table className="table">
                <TableHead<TModel> model={m} />
                <TableBody<TModel> model={m} />
            </table>
        );
    }
}

class TableHead<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel> }> {
    srender({ model: m } : { model : ReservationsListBase<TModel> }) {
        return (
            <thead>
                <tr>
                    {GridTableHeaderCells(Columns, m.RowSorter)}
                    <th>{/* Empty header for actions column */}</th>
                </tr>
            </thead>
        );
    }
}

class TableBody<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel> }> {
    srender({ model: m } : { model : ReservationsListBase<TModel> }) {
        return (
            <tbody>
                {m.Rows().map(row =>
                    <TableRow<TModel> row={row} model={m} key={row.Model.Id()} />
                )}
            </tbody>
        );
    }
}

class TableRow<TModel> extends SuperCriticalComponent<{ row : RowModel, model : ReservationsListBase<TModel> }> {
    srender({ row, model: m } : { row : RowModel, model : ReservationsListBase<TModel>}) {
        return (
            <tr>
                {GridTableRowCells(Columns, row)}
                <td className="actions">
                    {m.PageAction() === PageAction.View ?
                        <a href="" className="fa fa-bars actions-link" onClick={noDefault(() => row.ShowActionsMenu(true))}></a>
                    :
                        <input type="checkbox" checked={row.SelectedForPageAction()} onChange={e => row.SelectedForPageAction(e.currentTarget.checked)} />
                    }
                    <RowActionsMenu row={row} />
                </td>
            </tr>
        );
    }
}

const RowActionsMenu = SuperCritical(({ row } : { row : RowModel }) =>
    row.ShowActionsMenu() ?
        <PCDismissible className="pc-card actions-menu" onDismiss={() => row.ShowActionsMenu(false)}>
            <div className="action-item">
                {row.FirstLine() && <a href={'#!schedulegrid?resid=' + row.Model.Id() + '&date=' + tzmoment(row.FirstLine()!).add(-1, 'days').format('YYYY-MM-DD')}>Go to Berths</a>}
            </div>
            <div className="action-item">
                {row.PortCallId() !== null && <a href={'#!portcallprofile?id=' + row.PortCallId()}>Go to PortCall</a>}
            </div>
        </PCDismissible>
    : null
);

class CancellationRemarksModal<TModel> extends SuperCriticalComponent<{ model : ReservationsListBase<TModel> }> {
    srender({ model: m } : { model : ReservationsListBase<TModel> }) {
        const prompt = m.CancellationRemarksPrompt();
        return (
            <PCModal className="cancellation-remarks-modal" shown={() => !!prompt} onBackDropClick={prompt?.QuitChanges}>
                <div className="modal-content pc-card">
                    <div className="modal-body">
                        <h2 className="modal-title">Cancel Reservations</h2>
                        <div>
                            Are you sure you want to cancel the following reservation(s)?
                        </div>
                        {prompt &&
                            <div>
                                <div className="remarks-shell">
                                    <textarea
                                        className="remarks"
                                        value={prompt.Remarks() ?? ''}
                                        onChange={e => prompt.Remarks(e.currentTarget.value || null)}
                                        placeholder="These remarks will be sent to editors on each PortCall."
                                        rows={3}
                                    />
                                </div>
                                <div className="actions">
                                    <button className="close pc-modal-close close-btn" onClick={prompt.QuitChanges}>Close</button>
                                    <button className="pc-button" onClick={noDefault(prompt.CancelReservations)}>Cancel</button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </PCModal>
        );
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/reservationslistbase/view.tsx